import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowUpRight } from 'lucide-react';
import { Link, useLocation } from 'react-router';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

import { invoiceDetailKey, useInvoiceDetail } from '../api/use-invoice-detail';
import { CompletePlaceholderDialog } from '../dialogs/complete-placeholder-dialog';
import { DiscardInvoiceButton } from '../dialogs/discard-invoice';
import { IssueFlowButton } from '../dialogs/issue-flow';
import { VoidButton } from '../dialogs/void-invoice-dialog';
import { useInvoiceDrawer } from '../hooks/use-invoice-drawer';
import { formatAmount } from '../lib/format-amount';
import { invoiceVerbsFor } from '../lib/invoice-actions';
import { capitalize } from '../lib/invoice-detail-fields';
import { ConnectEasyBillButton } from './connect-easybill-button';
import { DescriptionRow, InvoiceInformationSection } from './invoice-detail-information';
import { ManageActions, useDrawerDiscard } from './invoice-detail-manage';
import { PaymentsSection } from './invoice-detail-payments';
import { DraftInvoicePreviewSection } from './invoice-draft-preview';

import type {
  ApprovalRequestRow,
  EasyBillEventRow,
  InvoiceDetail,
} from '../api/use-invoice-detail';
import type { InvoiceRow } from '../types';

export function InvoiceDetailDrawer() {
  const { invoiceId, close } = useInvoiceDrawer();
  const open = invoiceId !== null;
  const { data, isLoading, isError, error } = useInvoiceDetail(open ? invoiceId : null);

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <SheetContent side="right" className="w-full sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{renderTitle(data?.invoice)}</SheetTitle>
          <SheetDescription>{renderSubtitle(data?.invoice)}</SheetDescription>
        </SheetHeader>
        <div className="space-y-6 overflow-y-auto px-4 pb-6">
          {isLoading ? <LoadingState /> : null}
          {isError ? <ErrorState error={error} /> : null}
          {data !== undefined ? <InvoiceDetailBody detail={data} /> : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function renderTitle(invoice: InvoiceRow | undefined): string {
  if (invoice === undefined) return 'Invoice';
  const parts: string[] = [invoice.invoice_number ?? 'Untitled invoice'];
  if (invoice.invoice_type !== null) parts.push(`· ${invoice.invoice_type}`);
  return parts.join(' ');
}

function renderSubtitle(invoice: InvoiceRow | undefined): string {
  if (invoice === undefined) return '';
  const parts: string[] = [];
  if (invoice.invoice_direction !== null) parts.push(capitalize(invoice.invoice_direction));
  if (invoice.company_name !== null) parts.push(invoice.company_name);
  if (invoice.total_amount !== null) {
    parts.push(formatAmount(invoice.total_amount, invoice.currency));
  }
  return parts.join(' · ');
}

function LoadingState() {
  return <p className="text-sm text-muted-foreground">Loading invoice…</p>;
}

function ErrorState({ error }: Readonly<{ error: Error | null }>) {
  return (
    <p className="text-sm text-destructive" role="alert">
      Could not load invoice. {error?.message ?? ''}
    </p>
  );
}

function InvoiceDetailBody({ detail }: Readonly<{ detail: InvoiceDetail }>) {
  return (
    <>
      {detail.invoice.is_placeholder ? <PlaceholderBanner invoice={detail.invoice} /> : null}
      <InvoiceInformationSection invoice={detail.invoice} />
      <ManageActions invoice={detail.invoice} />
      <DraftInvoicePreviewSection key={detail.invoice.id} invoice={detail.invoice} />
      <LifecycleActions invoice={detail.invoice} />
      <PaymentsSection invoice={detail.invoice} payments={detail.payments} />
      <ApprovalRequestsSection requests={detail.approval_requests} />
      <EasyBillEventSection invoice={detail.invoice} event={detail.easybill_event} />
    </>
  );
}

// The Lifecycle group: Issue (with the review step folded in), Discard
// (cancel a Draft / soft-delete a never-issued imported row), and Void.
// Renders from the consolidated verb model and is hidden once none apply.
// On success the invoice-detail query is invalidated so the drawer reflects
// the new state; a discard also closes the drawer since the row is gone.
function LifecycleActions({ invoice }: Readonly<{ invoice: InvoiceRow }>) {
  const queryClient = useQueryClient();
  const onDiscarded = useDrawerDiscard(invoice);
  const { pathname } = useLocation();
  const verbs = new Set(invoiceVerbsFor(invoice).map((entry) => entry.verb));
  if (!verbs.has('issue') && !verbs.has('discard') && !verbs.has('void')) return null;
  // An advance spans the whole order — a BL page only carries its allocated
  // share, so issuing (or discarding/voiding) from here would act on the full
  // order value under a partial figure. The verbs stay on the order page; the
  // link reopens this drawer there.
  const orderLevelId =
    invoice.invoice_type === 'Advance' && pathname.startsWith('/bl-orders/')
      ? invoice.order_id
      : null;
  const onChanged = () => {
    void queryClient.invalidateQueries({ queryKey: invoiceDetailKey(invoice.id) });
  };
  return (
    <section aria-labelledby="lifecycle-heading" className="space-y-2">
      <h3 id="lifecycle-heading" className="text-sm font-semibold">
        LIFECYCLE
      </h3>
      {orderLevelId !== null ? (
        <AdvanceOrderLevelLink orderId={orderLevelId} invoiceId={invoice.id} />
      ) : (
        <div className="flex flex-wrap gap-2">
          <IssueFlowButton invoice={invoice} onIssued={onChanged} />
          <DiscardInvoiceButton invoice={invoice} onDiscarded={onDiscarded} />
          <VoidButton invoice={invoice} onVoided={onChanged} />
        </div>
      )}
    </section>
  );
}

function AdvanceOrderLevelLink({
  orderId,
  invoiceId,
}: Readonly<{ orderId: string; invoiceId: string }>) {
  return (
    <div className="space-y-2">
      <p className="text-muted-foreground text-sm">
        This advance covers the whole order — only its allocated share applies to this BL. Issue or
        discard it from the order page.
      </p>
      <Button asChild variant="outline" size="sm" className="gap-1">
        <Link
          to={`/inventory/${orderId}?invoice=${invoiceId}`}
          data-testid={`invoice-${invoiceId}-manage-at-order`}
        >
          <ArrowUpRight className="size-4" aria-hidden />
          Manage at order level
        </Link>
      </Button>
    </div>
  );
}

function PlaceholderBanner({ invoice }: Readonly<{ invoice: InvoiceRow }>) {
  const [open, setOpen] = useState(false);
  return (
    <section
      aria-labelledby="placeholder-heading"
      className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100"
    >
      <h3 id="placeholder-heading" className="text-sm font-semibold">
        Placeholder invoice
      </h3>
      <p className="text-sm">
        This invoice was created to anchor a payment before the supplier document arrived. Complete
        it to record an issue date, invoice number, and type — then it can be issued normally.
      </p>
      <Button
        size="sm"
        onClick={() => {
          setOpen(true);
        }}
      >
        Complete placeholder
      </Button>
      <CompletePlaceholderDialog open={open} onOpenChange={setOpen} invoiceId={invoice.id} />
    </section>
  );
}

function ApprovalRequestsSection({ requests }: Readonly<{ requests: ApprovalRequestRow[] }>) {
  if (requests.length === 0) return null;
  return (
    <section aria-labelledby="approvals-heading" className="space-y-2">
      <h3 id="approvals-heading" className="text-sm font-semibold">
        Approval requests
      </h3>
      <ul className="divide-y rounded-md border">
        {requests.map((r) => (
          <li key={r.id} className="space-y-1 px-3 py-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="font-medium">{r.rule_label}</span>
              <Badge variant="outline">{r.status}</Badge>
            </div>
            {r.warning_message !== null ? (
              <p className="text-muted-foreground">{r.warning_message}</p>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

// Sell-side invoices carry the outbound Connect control; once connected, the
// invoice shows the connected state immediately while the inbound webhook's
// `easybill_event` reconciliation rows arrive later. Hidden entirely for a
// buy-side invoice with no event and no linkage.
function EasyBillEventSection({
  invoice,
  event,
}: Readonly<{ invoice: InvoiceRow; event: EasyBillEventRow | null }>) {
  const isSell = invoice.invoice_direction === 'Sale';
  const connected = invoice.easybill_document_id !== null;
  if (!isSell && event === null && !connected) return null;
  return (
    <section aria-labelledby="easybill-heading" className="space-y-2">
      <h3 id="easybill-heading" className="text-sm font-semibold">
        EasyBill linkage
      </h3>
      {isSell ? (
        <div className="flex flex-wrap gap-2">
          <ConnectEasyBillButton invoice={invoice} />
        </div>
      ) : null}
      {connected && event === null ? (
        <p className="text-sm text-muted-foreground">
          Connected — awaiting reconciliation from EasyBill.
        </p>
      ) : null}
      {event !== null ? (
        <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
          <DescriptionRow label="Document" value={event.easybill_document_number} />
          <DescriptionRow label="Document type" value={event.easybill_document_type} />
          <DescriptionRow label="Event type" value={event.event_type} />
          <DescriptionRow label="Reconciliation" value={event.reconciliation_status} chip />
          <DescriptionRow label="Confidence" value={event.confidence_score} />
          <DescriptionRow label="Test" value={event.is_test ? 'Yes' : 'No'} />
        </dl>
      ) : null}
    </section>
  );
}
